import { Component, Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { forkJoin, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { IMenu, MenuFile } from '../../../interfaces/menu';
import { ConfirmationDialogComponent } from '../confirm-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';

type MenuLanguage = string | null;
type ExistingFileValue = File | string | null;

type MenuForm = {
  id: FormControl<number | null>;
  name: FormControl<string>;
  language: FormControl<MenuLanguage>;
  hidden: FormControl<boolean>;
  menuFile: FormControl<File | MenuFile | null>;
  iconFile: FormControl<ExistingFileValue>;
  bannerFile: FormControl<ExistingFileValue>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-menu',
  templateUrl: './edit-menu.component.html',
  styleUrls: ['./edit-menu.component.scss'],
})
export class EditMenuComponent {
  public selected = 0;
  public languages = [
    { value: 'en-CA', viewValue: 'English' },
    { value: 'fr-CA', viewValue: 'French' },
    { value: null, viewValue: 'Not Set' },
  ];
  public menus = this.data.menusGroup;
  public isMainMenu = this.data.isMainMenu;
  public happyHours = this.data.menusGroup?.[0].happyHours ?? false;
  public menusFormArray: FormArray<FormGroup<MenuForm>>;
  public isUpdating = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      restaurantId: number;
      isMainMenu: boolean;
      originalId: number;
      menusGroup: IMenu[];
    },
    private menuState: MenuStoreService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<EditMenuComponent>,
  ) {
    this.menusFormArray = new FormArray<FormGroup<MenuForm>>(
      this.data.menusGroup.map(menu => this.createMenuFormGroup(menu))
    );
  }

  private createMenuFormGroup(menu: IMenu): FormGroup<MenuForm> {
    return new FormGroup<MenuForm>({
      id: new FormControl(menu.id, { nonNullable: true }),
      name: new FormControl(menu.name, { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
      language: new FormControl<MenuLanguage>(menu.language),
      hidden: new FormControl(menu.hidden, { nonNullable: true }),
      menuFile: new FormControl<File | MenuFile | null>(menu.file || null, [Validators.required]),
      iconFile: new FormControl<ExistingFileValue>(menu.icon || null),
      bannerFile: new FormControl<ExistingFileValue>(menu.banner || null)
    });
  }

  get menusControls(): Array<FormGroup<MenuForm>> {
    return this.menusFormArray.controls;
  }

  public menuPreviewUrl(index: number): string {
      return this.menus[index]?.pages?.[0] ?? '';
  }

  public iconPreviewUrl(index: number): string {
    return this.menus[index]?.icon ?? '';
  }

  public bannerPreviewUrl(index: number): string {
    return this.menus[index]?.banner ?? '';
  }

  get selectedLanguage(): MenuLanguage {
    return this.menusControls[this.selected].controls.language.value;
  }

  public isLanguageAvailable(language: MenuLanguage, currentIndex: number): boolean {
    const selectedLanguages = this.menusControls
      .map((menu, index) => index !== currentIndex ? menu.controls.language.value : null)
      .filter(lang => lang !== null);

    return !selectedLanguages.includes(language) && !(this.menusControls.length > 1 && language === null);
  }

  public addTranslate(): void {
    this.menusFormArray.push(this.createNewTranslationFormGroup());

    this.selected = this.menusFormArray.length - 1;
  }

  public removeTranslate(index: number, id: number | null): void {
    if (id) {
      const title = this.translate.instant('pages.menu.modal.delete');
      const message = this.translate.instant('pages.menu.messages.confirmDelete');
      const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message,
          title
        },
        panelClass: 'confirmation-modal'
      });

      confirmationModal.afterClosed().pipe(
        filter(Boolean),
        untilDestroyed(this)
      ).subscribe((): void => {
        this.delete(this.data.restaurantId, id, index);
      });
    } else {
      this.menusFormArray.removeAt(index);
      this.selected = index;
    }
  }

  update(): void {
    this.isUpdating = true;

    const [menuOriginalData, ...menuTranslatedData] = this.menusControls;
    const [originalMenu, ...translatedMenus] = this.data.menusGroup;
    const originalMenuId = originalMenu.id;

    const updateMenu = (menuControl: FormGroup<MenuForm>) => {
      const menuData = menuControl.getRawValue();

      if (menuData.id === null) {
        throw new Error('Menu id is required');
      }

      return this.menuState.updateMenu(
        menuData.id,
        this.data.restaurantId,
        {
          name: menuData.name,
          language: menuData.language,
          happyHours: this.happyHours,
          hidden: menuData.hidden,
          menuFile: menuData.menuFile,
          iconFile: menuData.iconFile,
          bannerFile: menuData.bannerFile
        }
      );
    };

    const addMenu = (menuControl: FormGroup<MenuForm>, originalId: number | null) => {
      const menuData = menuControl.getRawValue();

      if (!(menuData.menuFile instanceof File)) {
        throw new Error('Menu file is required');
      }

      return this.menuState.addMenu(
        this.data.restaurantId,
        menuData.name,
        menuData.language,
        this.happyHours,
        menuData.hidden,
        menuData.menuFile,
        originalId,
        menuData.iconFile instanceof File ? menuData.iconFile : undefined,
        menuData.bannerFile instanceof File ? menuData.bannerFile : undefined
      );
    };

    const hasOriginalChanged = this.isChangedOriginal();
    const changedTranslations: Array<FormGroup<MenuForm>> = menuTranslatedData
      .map((menuControl, index) => {
        const menu = translatedMenus[index];
        return this.isMenuChanged(menuControl, menu) ? menuControl : null;
      })
      .filter((ctrl): ctrl is FormGroup<MenuForm> => ctrl !== null);

    const onComplete = () => {
      this.dialogRef?.close?.();
      this.isUpdating = false;
    };

    const onError = () => {
      this.isUpdating = false;
    };

    if (!hasOriginalChanged && changedTranslations.length === 0) {
      this.isUpdating = false;
      return;
    }

    const handleTranslations = (originalId: number) => {

      if (changedTranslations.length === 0) {
        return of(null);
      }

      return forkJoin(changedTranslations.map(menuControl => {
        return menuControl.controls.id.value
          ? updateMenu(menuControl)
          : addMenu(menuControl, originalId);
      }));
    };

    const observable$ = hasOriginalChanged
      ? updateMenu(menuOriginalData).pipe(
        switchMap(updatedOriginal => handleTranslations(updatedOriginal.id))
      )
      : handleTranslations(originalMenuId);

    observable$.pipe(
      untilDestroyed(this)
    ).subscribe({
      next: () => onComplete(),
      error: () => onError()
    });
  }

  delete(restaurantId: number, menuId: number, index: number): void {
    this.isUpdating = true;
    this.menuState
      .removeRestaurantsMenu(restaurantId, menuId)
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
            this.menusFormArray.removeAt(index);

            if (this.menusFormArray.length === 0 || this.data.originalId === menuId) {
              this.dialogRef.close();
            } else {
              this.selected = index;
              this.isUpdating = false;
            }
          },
        () => {
          this.isUpdating = false;
          this.dialogRef.close();
        }
      );
  }

  public isChangedOriginal(): boolean {
    return this.isMenuChanged(this.menusControls[0], this.data.menusGroup[0]);
  }

  public isChangedTranslations(): boolean {
    const menuTranslationData = this.menusControls.slice(1);
    const translation = this.data.menusGroup.slice(1);

    if (menuTranslationData.length !== translation.length) {
      return true;
    }

    return menuTranslationData.some((menuControl, index) =>
      this.isMenuChanged(menuControl, translation[index])
    );
  }

  private createNewTranslationFormGroup(): FormGroup<MenuForm> {
    return new FormGroup<MenuForm>({
      id: new FormControl<number | null>(null),
      name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
      language: new FormControl<MenuLanguage>(null, [Validators.required]),
      hidden: new FormControl(false, { nonNullable: true }),
      menuFile: new FormControl<File | MenuFile | null>(null, [Validators.required]),
      iconFile: new FormControl<ExistingFileValue>(null),
      bannerFile: new FormControl<ExistingFileValue>(null)
    });
  }

  private isMenuChanged(menuControl: FormGroup<MenuForm>, menuData?: IMenu): boolean {
    const menuValue = menuControl.getRawValue();
    const isNew = !menuValue.id;

    return isNew || (
      menuValue.name !== menuData?.name ||
      menuValue.language !== menuData?.language ||
      menuValue.hidden !== menuData?.hidden ||
      menuValue.menuFile !== menuData?.file ||
      menuValue.iconFile !== menuData?.icon ||
      menuValue.bannerFile !== menuData?.banner ||
      this.happyHours !== menuData?.happyHours
    );
  }
}
